import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import {
  X, CalendarDays, ChevronDown, ChevronRight, MapPin, Video, Phone, Users, UserPlus,
  FileText, Paperclip, MoreHorizontal, Bold, Italic, List, ListOrdered, Link2,
  ListChecks, CheckCircle2, Check, Plus, Trash2, Pencil, ExternalLink, Building2,
  FileArchive, Image as ImageIcon, File as FileIcon, RotateCcw, CalendarRange, Upload,
  Briefcase, Copy, CalendarClock, XCircle, ListTodo,
} from "lucide-react";
import api, { apiError, API } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const toHHMM = (m) => `${String(Math.max(0, Math.floor(m / 60))).padStart(2, "0")}:${String(Math.max(0, m % 60)).padStart(2, "0")}`;
const durLabel = (min) => (min >= 60 ? `${Math.floor(min / 60)}h${String(min % 60).padStart(2, "0")}` : `${min} min`);
const initials = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "·";
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const fmtDate = (iso) => { try { return format(parseISO(iso), "d MMMM yyyy", { locale: fr }); } catch { return iso; } };

// Transforme une action avant/après RDV en tâche de la To-do centrale (liée au RDV)
const actionToTodo = async (rdv, which, a) => {
  try {
    await api.post("/tasks", {
      title: `${which === "actions_before" ? "Avant RDV" : "Après RDV"} — ${a.label}`,
      client_id: rdv.client_id || null, deadline: a.deadline || rdv.date || null,
      assignee: a.assignee || undefined, service: "Commercial",
      source_type: "appointment", source_id: rdv.id,
      source_label: `Rendez-vous — ${rdv.title || "RDV"}`, source_url: "/rdv",
    });
    toast.success("Tâche ajoutée à la To-do");
    window.dispatchEvent(new Event("sf:tasks"));
  } catch { toast.error("Création impossible"); }
};
const fmtSize = (b = 0) => (b >= 1048576 ? `${(b / 1048576).toFixed(1).replace(".", ",")} Mo` : b >= 1024 ? `${Math.round(b / 1024)} Ko` : `${b} o`);
const mondayISO = (isoDate) => {
  const d = new Date(isoDate + "T12:00:00");
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const STATUS_OPTIONS = {
  a_venir: { label: "À venir", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  confirme: { label: "Confirmé", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  en_cours: { label: "En cours", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  termine: { label: "Terminé", cls: "bg-slate-100 text-slate-500 border-slate-300" },
  annule: { label: "Annulé", cls: "bg-red-100 text-red-600 border-red-200" },
};

const REPORT_STATUS = {
  non_commence: { label: "Non commencé", cls: "bg-neutral-100 text-neutral-500 border-neutral-200" },
  brouillon: { label: "Brouillon", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  finalise: { label: "Finalisé", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  envoye: { label: "Envoyé au client", cls: "bg-blue-100 text-blue-700 border-blue-200" },
};

const AVATAR_COLORS = [
  ["#DBEAFE", "#1D4ED8"], ["#FEF3C7", "#B45309"], ["#DCFCE7", "#15803D"],
  ["#FFEDD5", "#C2410C"], ["#F3E8FF", "#6B21A8"], ["#FCE7F3", "#BE185D"],
];
const avatarColor = (name = "") => AVATAR_COLORS[[...name].reduce((s, c) => s + c.charCodeAt(0), 0) % AVATAR_COLORS.length];

const visioProvider = (link = "") => {
  if (/meet\.google/i.test(link)) return "Visio Google Meet";
  if (/zoom\./i.test(link)) return "Visio Zoom";
  if (/teams\./i.test(link)) return "Visio Microsoft Teams";
  return "Visio en ligne";
};

const attMeta = (name = "") => {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if (ext === "pdf") return { Icon: FileText, bg: "#FEE2E2", fg: "#DC2626", label: "PDF" };
  if (["zip", "rar", "7z"].includes(ext)) return { Icon: FileArchive, bg: "#FEF3C7", fg: "#B45309", label: ext.toUpperCase() };
  if (["jpg", "jpeg", "png", "gif", "webp", "svg", "heic"].includes(ext)) return { Icon: ImageIcon, bg: "#DBEAFE", fg: "#1D4ED8", label: ext.toUpperCase() };
  return { Icon: FileIcon, bg: "#F1F5F9", fg: "#64748B", label: ext ? ext.toUpperCase() : "FICHIER" };
};

function Avatar({ name, size = "w-10 h-10 text-xs" }) {
  const [bg, fg] = avatarColor(name);
  return (
    <span title={name} className={`${size} rounded-full flex items-center justify-center font-bold shrink-0 border`}
      style={{ background: bg, color: fg, borderColor: `${fg}33` }}>
      {initials(name)}
    </span>
  );
}

function ToolBtn({ icon: Icon, label, testid, onClick }) {
  return (
    <button type="button" title={label} data-testid={testid}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      className="p-1.5 rounded-md hover:bg-neutral-200/60 text-neutral-500 transition-colors">
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}

function ActionSection({ title, which, rdv, draft, onDraft, onAdd, onToggle, onRemove }) {
  const items = rdv[which] || [];
  const today = new Date().toISOString().slice(0, 10);
  return (
    <section data-testid={`rdv-fiche-${which}`}>
      <h3 className="flex items-center gap-2 font-heading font-bold text-[15px] text-[#0F172A] mb-2.5">
        <ListChecks className="w-4 h-4 text-neutral-500" /> {title}
      </h3>
      <div className="space-y-1.5">
        {items.length === 0 && <p className="text-xs text-neutral-400" data-testid={`rdv-fiche-${which}-empty`}>Aucune action pour l'instant.</p>}
        {items.map((a) => {
          const overdue = a.deadline && a.deadline < today && !a.done;
          return (
            <div key={a.id} className="flex items-center gap-3 group" data-testid={`rdv-fiche-action-${a.id}`}>
              <button onClick={() => onToggle(which, a.id)} data-testid={`rdv-fiche-action-check-${a.id}`}
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${a.done ? "bg-emerald-500 border-emerald-500" : "border-neutral-300 hover:border-emerald-400"}`}>
                {a.done && <Check className="w-3.5 h-3.5 text-white" />}
              </button>
              <p className={`flex-1 min-w-0 text-sm truncate ${a.done ? "line-through text-neutral-400" : "text-[#0F172A]"}`}>{a.label}</p>
              {a.assignee && <Avatar name={a.assignee} size="w-6 h-6 text-[9px]" />}
              {a.deadline && <span className={`text-xs whitespace-nowrap ${overdue ? "text-red-600 font-semibold" : "text-neutral-400"}`}>{fmtDate(a.deadline)}</span>}
              <button onClick={() => actionToTodo(rdv, which, a)} data-testid={`rdv-fiche-action-todo-${a.id}`}
                className="opacity-0 group-hover:opacity-100 p-1 rounded text-neutral-300 hover:text-[#1D4ED8] transition-all shrink-0" title="Créer une tâche dans la To-do">
                <ListTodo className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => onRemove(which, a.id)} data-testid={`rdv-fiche-action-del-${a.id}`}
                className="opacity-0 group-hover:opacity-100 p-1 rounded text-neutral-300 hover:text-red-500 transition-all shrink-0" title="Retirer">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
        <div className="flex items-center gap-2 pt-1">
          <Plus className="w-4 h-4 text-neutral-300 shrink-0" />
          <input data-testid={`rdv-fiche-${which}-add-label`} value={draft.label}
            onChange={(e) => onDraft(which, { ...draft, label: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && onAdd(which)}
            placeholder="Ajouter une action…" className="flex-1 min-w-0 text-sm py-1 focus:outline-none placeholder:text-neutral-300" />
          <input data-testid={`rdv-fiche-${which}-add-assignee`} value={draft.assignee}
            onChange={(e) => onDraft(which, { ...draft, assignee: e.target.value })}
            placeholder="Assigné" className="w-24 sm:w-28 text-xs border border-neutral-200 rounded-lg px-2 py-1 focus:outline-none focus:border-neutral-400" />
          <input data-testid={`rdv-fiche-${which}-add-deadline`} type="date" value={draft.deadline}
            onChange={(e) => onDraft(which, { ...draft, deadline: e.target.value })}
            className="w-32 text-xs border border-neutral-200 rounded-lg px-2 py-1 text-neutral-500 focus:outline-none focus:border-neutral-400" />
          <button onClick={() => onAdd(which)} data-testid={`rdv-fiche-${which}-add-btn`}
            className="text-xs font-semibold text-[#0369A1] hover:underline shrink-0">Ajouter</button>
        </div>
      </div>
    </section>
  );
}

export default function RdvFiche({ rdv: initial, onClose, onChanged, variant = "modal" }) {
  const navigate = useNavigate();
  const [rdv, setRdv] = useState(initial);
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [report, setReport] = useState(initial.report || "");
  const [reportSaved, setReportSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [addPartOpen, setAddPartOpen] = useState(false);
  const [partForm, setPartForm] = useState({ name: "", role: "" });
  const [projects, setProjects] = useState([]);
  const [drafts, setDrafts] = useState({
    actions_before: { label: "", assignee: "", deadline: "" },
    actions_after: { label: "", assignee: "", deadline: "" },
  });
  const reportRef = useRef(null);
  const fileRef = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/appointments");
      const fresh = (data || []).find((a) => a.id === initial.id);
      if (fresh) setRdv(fresh);
      onChanged?.();
    } catch { /* silencieux */ }
  };
  useEffect(() => { setRdv(initial); }, [initial]);
  useEffect(() => { // changement de RDV : contenu actualisé sans fermer le panneau
    setReport(initial.report || "");
    setEditing(false);
    setAddPartOpen(false);
    refresh();
  }, [initial.id]); // eslint-disable-line
  useEffect(() => {
    api.get("/clients").then((r) => setClients((r.data || []).filter((c) => !c.archived))).catch(() => {});
    Promise.all([
      api.get("/graphisme/projects").then((r) => r.data || []).catch(() => []),
      api.get("/web/projects").then((r) => r.data || []).catch(() => []),
    ]).then(([g, w]) => setProjects([
      ...g.map((p) => ({ kind: "graphisme", id: p.id, label: p.name, client_id: p.client_id, client_name: p.client_name })),
      ...w.map((p) => ({ kind: "web", id: p.id, label: p.name, client_id: p.client_id, client_name: p.client_name })),
    ]));
  }, []);

  const client = clients.find((c) => c.id === rdv.client_id) || null;
  const contacts = Array.isArray(client?.contacts) ? client.contacts : [];
  const todayIso = new Date().toISOString().slice(0, 10);
  const status = STATUS_OPTIONS[rdv.rdv_status] ? rdv.rdv_status
    : rdv.completed ? "termine"
    : rdv.booking_status === "annule" ? "annule"
    : (rdv.date || "") < todayIso ? "en_cours" : "a_venir";
  const reportStatus = REPORT_STATUS[rdv.report_status] ? rdv.report_status
    : (rdv.report || "").trim() ? "brouillon" : "non_commence";
  const projectOptions = projects.filter((p) => !rdv.client_id || p.client_id === rdv.client_id || (client && p.client_name === client.name));
  const projectValue = rdv.project ? `${rdv.project.kind}:${rdv.project.id || "cm"}` : "";
  const rdvDur = rdv.rdv_minutes || rdv.duration_minutes || 60;

  const patchRdv = async (fields, silent = false) => {
    try {
      await api.patch(`/appointments/${rdv.id}`, fields);
      await refresh();
      return true;
    } catch (e) { if (!silent) toast.error(apiError(e)); return false; }
  };

  const setStatus = async (v) => {
    const ok = await patchRdv({ rdv_status: v, completed: v === "termine" });
    if (ok) toast.success(v === "termine" ? "RDV marqué comme terminé" : "Statut mis à jour");
  };
  const duplicateRdv = async () => {
    try {
      await api.post(`/appointments/${rdv.id}/duplicate`);
      toast.success("RDV dupliqué — à replacer par glisser-déposer dans l'agenda");
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const cancelRdv = async () => {
    if (!window.confirm("Annuler ce RDV ? Le créneau sera libéré dans l'agenda (le RDV reste dans l'historique).")) return;
    const ok = await patchRdv({ rdv_status: "annule", completed: false });
    if (ok) toast.success("RDV annulé — créneau libéré dans l'agenda");
  };
  const setProject = async (key) => {
    if (!key) { await patchRdv({ project: null }); return; }
    if (key === "cm:cm") {
      await patchRdv({ project: { kind: "cm", id: null, label: `Community Management — ${client?.name || rdv.contact_name || "client"}` } });
      return;
    }
    const p = projectOptions.find((x) => `${x.kind}:${x.id}` === key);
    if (p) await patchRdv({ project: { kind: p.kind, id: p.id, label: p.label } });
  };
  const setReportStatus = async (v) => {
    const ok = await patchRdv({ report_status: v });
    if (ok) toast.success(`Compte rendu : ${REPORT_STATUS[v].label}`);
  };

  /* ---------- Édition inline ---------- */
  const startEdit = () => {
    setForm({
      title: rdv.title || "", type: rdv.type || "visio", date: rdv.date || "",
      start_time: toHHMM(rdv.start_min ?? 600), rdv_minutes: rdv.rdv_minutes ?? 60,
      travel_before_min: rdv.travel_before_min ?? 0, travel_after_min: rdv.travel_after_min ?? 0,
      client_id: rdv.client_id || "", address: rdv.address || "", visio_link: rdv.visio_link || "",
      contact_name: rdv.contact_name || "", email: rdv.email || "", phone: rdv.phone || "",
      description: rdv.description || "",
    });
    setEditing(true);
  };
  const saveEdit = async () => {
    if (!form.title.trim() || !form.date || !form.start_time) { toast.error("Titre, date et heure requis"); return; }
    if (form.type === "exterieur" && !form.address.trim()) { toast.error("Indiquez le lieu du RDV extérieur"); return; }
    setSaving(true);
    const ok = await patchRdv({
      ...form,
      rdv_minutes: Number(form.rdv_minutes) || 60,
      travel_before_min: Number(form.travel_before_min) || 0,
      travel_after_min: Number(form.travel_after_min) || 0,
    });
    setSaving(false);
    if (ok) { toast.success("RDV mis à jour — agenda synchronisé"); setEditing(false); }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer ce RDV ? Le créneau sera libéré dans l'agenda.")) return;
    try {
      await api.delete(`/appointments/${rdv.id}`);
      toast.success("RDV supprimé");
      onChanged?.(); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };

  /* ---------- Participants ---------- */
  const saveParticipants = (list) => patchRdv({ participants: list });
  const addParticipant = () => {
    if (!partForm.name.trim()) { toast.error("Indiquez le nom du participant"); return; }
    saveParticipants([...(rdv.participants || []), { name: partForm.name.trim(), role: partForm.role.trim() }]);
    setPartForm({ name: "", role: "" });
    setAddPartOpen(false);
  };
  const removeParticipant = (idx) => saveParticipants((rdv.participants || []).filter((_, i) => i !== idx));

  /* ---------- Pièces jointes ---------- */
  const attUrl = (att, dl) => `${API}/appointments/${rdv.id}/attachments/${att.id}${dl ? "?dl=1" : ""}`;
  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/appointments/${rdv.id}/attachments`, fd);
      toast.success("Fichier ajouté");
      await refresh();
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const delAttachment = async (att) => {
    if (!window.confirm(`Supprimer « ${att.name} » ?`)) return;
    try {
      await api.delete(`/appointments/${rdv.id}/attachments/${att.id}`);
      toast.success("Pièce jointe supprimée");
      await refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  /* ---------- Compte rendu ---------- */
  const saveReport = async () => {
    if (report === (rdv.report || "")) return;
    try {
      const fields = { report };
      if (report.trim() && !["finalise", "envoye"].includes(rdv.report_status || "non_commence")) fields.report_status = "brouillon";
      await api.patch(`/appointments/${rdv.id}`, fields);
      setReportSaved(true);
      setTimeout(() => setReportSaved(false), 2500);
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const wrapSelection = (before, after = before) => {
    const ta = reportRef.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e, value } = ta;
    const sel = value.slice(s, e) || "texte";
    setReport(value.slice(0, s) + before + sel + after + value.slice(e));
    requestAnimationFrame(() => { ta.focus(); ta.setSelectionRange(s + before.length, s + before.length + sel.length); });
  };
  const prefixLines = (fn) => {
    const ta = reportRef.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e, value } = ta;
    const start = value.lastIndexOf("\n", Math.max(0, s - 1)) + 1;
    const endIdx = value.indexOf("\n", e);
    const end = endIdx === -1 ? value.length : endIdx;
    const lines = value.slice(start, end).split("\n").map((l, i) => (l.trim() ? fn(i) + l : l));
    setReport(value.slice(0, start) + lines.join("\n") + value.slice(end));
    requestAnimationFrame(() => ta.focus());
  };

  /* ---------- Actions avant / après (synchronisées avec les Tâches) ---------- */
  const onDraft = (which, v) => setDrafts((d) => ({ ...d, [which]: v }));
  const saveActions = async (which, list) => patchRdv({ [which]: list });
  const addAction = async (which) => {
    const a = drafts[which];
    if (!a.label.trim()) { toast.error("Indiquez l'action"); return; }
    const action = { id: crypto.randomUUID(), label: a.label.trim(), assignee: a.assignee.trim(), deadline: a.deadline, done: false, task_id: null };
    try {
      const { data: t } = await api.post("/tasks", {
        title: action.label, deadline: action.deadline || null,
        client_id: rdv.client_id || null, appointment_id: rdv.id,
        assignee: action.assignee || undefined,
        group_label: client?.name || rdv.contact_name || rdv.company || rdv.title,
      });
      action.task_id = t.id;
    } catch { toast.info("Action enregistrée sur la fiche (tâche globale non créée)"); }
    const ok = await saveActions(which, [...(rdv[which] || []), action]);
    if (ok) {
      setDrafts((d) => ({ ...d, [which]: { label: "", assignee: "", deadline: "" } }));
      toast.success("Action ajoutée — visible aussi dans les Tâches");
    }
  };
  const toggleAction = async (which, id) => {
    const list = (rdv[which] || []).map((a) => (a.id === id ? { ...a, done: !a.done } : a));
    const act = list.find((a) => a.id === id);
    await saveActions(which, list);
    if (act?.task_id) {
      try { await api.patch(`/tasks/${act.task_id}`, { status: act.done ? "finalise" : "a_faire" }); } catch { /* non bloquant */ }
    }
  };
  const removeAction = async (which, id) => {
    const act = (rdv[which] || []).find((a) => a.id === id);
    await saveActions(which, (rdv[which] || []).filter((a) => a.id !== id));
    if (act?.task_id) {
      try { await api.delete(`/tasks/${act.task_id}`); } catch { /* non bloquant */ }
    }
  };

  /* ---------- Infos pratiques ---------- */
  const loc = rdv.type === "visio"
    ? { Icon: Video, label: visioProvider(rdv.visio_link), sub: rdv.visio_link, href: rdv.visio_link || null }
    : rdv.type === "telephonique"
      ? { Icon: Phone, label: "RDV téléphonique", sub: rdv.phone, href: rdv.phone ? `tel:${rdv.phone.replace(/\s/g, "")}` : null }
      : { Icon: MapPin, label: "Lieu extérieur", sub: rdv.address, href: null };
  const badgeLabel = rdv.prospect_id && !rdv.client_id ? "RDV PROSPECT" : "RDV CLIENT";
  const inputCls = "w-full text-sm border border-neutral-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-neutral-400";

  const isPanel = variant === "panel";
  return (
    <div className={isPanel ? "sticky top-3 z-30" : "fixed inset-0 z-[70] bg-black/40 flex items-start justify-center overflow-y-auto p-4"}
      onClick={isPanel ? undefined : onClose} data-testid="rdv-fiche">
      <div className={isPanel
        ? "bg-white w-full rounded-2xl border border-[#E2E8F0] shadow-sm overflow-hidden flex flex-col"
        : "bg-white w-full max-w-2xl rounded-2xl shadow-2xl my-6"}
        style={isPanel ? { maxHeight: "calc(100vh - 150px)" } : undefined}
        onClick={(e) => e.stopPropagation()}>
        {/* En-tête */}
        <div className="flex items-start justify-between px-6 sm:px-8 pt-6">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-[#DBEAFE] border border-[#BFDBFE] flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-[#1D4ED8]" />
            </span>
            <span className="inline-flex items-center gap-1.5 bg-[#EFF6FF] border border-[#BFDBFE] text-[#1D4ED8] text-[11px] font-bold uppercase tracking-wider rounded-full px-3 py-1" data-testid="rdv-fiche-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6]" />{badgeLabel}
            </span>
          </div>
          <div className="flex items-start gap-3">
            <p className="text-[#64748B] italic text-xs leading-snug text-right hidden sm:block">« Un bon planning,<br />c'est des projets qui avancent. »</p>
            <button onClick={onClose} data-testid="rdv-fiche-close" className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-400 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className={`px-6 sm:px-8 pt-4 pb-6 space-y-6 ${isPanel ? "overflow-y-auto flex-1 min-h-0" : ""}`}>
          {/* Titre + statut */}
          <div className="flex items-start justify-between gap-3">
            {editing ? (
              <input data-testid="rdv-fiche-edit-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="flex-1 font-heading text-2xl font-bold text-[#0F172A] border-b-2 border-neutral-300 focus:outline-none focus:border-[#0B132B] bg-transparent" />
            ) : (
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] leading-tight" data-testid="rdv-fiche-title">{rdv.title}</h1>
            )}
            <div className="relative shrink-0">
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="rdv-fiche-status"
                className={`appearance-none cursor-pointer text-xs font-semibold border rounded-full pl-3 pr-7 py-1.5 focus:outline-none ${STATUS_OPTIONS[status].cls}`}>
                {Object.entries(STATUS_OPTIONS).map(([id, o]) => <option key={id} value={id}>{o.label}</option>)}
              </select>
              <ChevronDown className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none opacity-60" />
            </div>
          </div>

          {/* Client */}
          {editing ? (
            <select data-testid="rdv-fiche-edit-client" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className={inputCls}>
              <option value="">Aucun client lié</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          ) : (
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center shrink-0">
                <Building2 className="w-4 h-4 text-[#64748B]" />
              </span>
              {client ? (
                <Link to={`/clients/${client.id}`} data-testid="rdv-fiche-client" onClick={onClose}
                  className="font-semibold text-[#0369A1] hover:underline flex items-center gap-0.5">
                  {client.name} <ChevronRight className="w-4 h-4" />
                </Link>
              ) : (
                <span className="font-semibold text-[#0F172A]" data-testid="rdv-fiche-client">{rdv.contact_name || rdv.prospect_name || "Aucun client lié"}</span>
              )}
            </div>
          )}

          {/* Projet associé */}
          <div className="flex items-center gap-2.5" data-testid="rdv-fiche-project-row">
            <span className="w-9 h-9 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center shrink-0">
              <Briefcase className="w-4 h-4 text-[#64748B]" />
            </span>
            <span className="text-xs font-medium text-neutral-400 shrink-0">Projet associé</span>
            <select value={projectValue} onChange={(e) => setProject(e.target.value)} data-testid="rdv-fiche-project"
              className="flex-1 min-w-0 text-sm font-semibold text-[#0F172A] bg-transparent focus:outline-none cursor-pointer truncate">
              <option value="">Aucun projet associé</option>
              {client && <option value="cm:cm">{`Community Management — ${client.name}`}</option>}
              {projectOptions.filter((p) => p.kind === "graphisme").length > 0 && (
                <optgroup label="Graphisme">
                  {projectOptions.filter((p) => p.kind === "graphisme").map((p) => <option key={p.id} value={`graphisme:${p.id}`}>{p.label}</option>)}
                </optgroup>
              )}
              {projectOptions.filter((p) => p.kind === "web").length > 0 && (
                <optgroup label="Web Design">
                  {projectOptions.filter((p) => p.kind === "web").map((p) => <option key={p.id} value={`web:${p.id}`}>{p.label}</option>)}
                </optgroup>
              )}
            </select>
          </div>

          {/* Bandeaux réservation en ligne */}
          {rdv.booked_online && ["a_reconfirmer", "annulation_demandee", "annule"].includes(rdv.booking_status) && (
            <div data-testid="rdv-fiche-booking-banner">
              {rdv.booking_status === "a_reconfirmer" && (
                <div className="flex items-center justify-between gap-2 bg-orange-50 border border-orange-200 rounded-xl px-3 py-2.5">
                  <p className="text-xs font-medium text-orange-800">Le client a demandé un report — nouveau créneau à reconfirmer.</p>
                  <button data-testid="rdv-fiche-confirm-btn" onClick={async () => { if (await patchRdv({ booking_status: "confirme" })) toast.success("RDV reconfirmé"); }}
                    className="text-[11px] font-semibold bg-orange-600 text-white rounded-md px-2.5 py-1.5 hover:bg-orange-700 transition-colors shrink-0">Reconfirmer</button>
                </div>
              )}
              {rdv.booking_status === "annulation_demandee" && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 space-y-1.5">
                  <p className="text-xs font-medium text-red-800">Le client souhaite annuler ce RDV.</p>
                  {rdv.cancel_reason && <p className="text-xs text-red-700">Motif : {rdv.cancel_reason}</p>}
                  <div className="flex gap-2 pt-0.5">
                    <button data-testid="rdv-fiche-cancel-accept-btn" onClick={async () => {
                      if (!window.confirm("Accepter l'annulation ? Le créneau sera libéré dans l'agenda.")) return;
                      await api.delete(`/appointments/${rdv.id}`);
                      toast.success("RDV annulé — créneau libéré");
                      onChanged?.(); onClose();
                    }} className="text-[11px] font-semibold bg-red-600 text-white rounded-md px-2.5 py-1.5 hover:bg-red-700 transition-colors">Accepter l'annulation</button>
                    <button data-testid="rdv-fiche-keep-btn" onClick={async () => { if (await patchRdv({ booking_status: "confirme" })) toast.success("RDV maintenu"); }}
                      className="text-[11px] font-semibold bg-white border border-neutral-300 rounded-md px-2.5 py-1.5 hover:bg-neutral-50 transition-colors">Maintenir le RDV</button>
                  </div>
                </div>
              )}
              {rdv.booking_status === "annule" && (
                <div className="bg-neutral-100 border border-neutral-200 rounded-xl px-3 py-2.5 space-y-1">
                  <p className="text-xs font-medium text-neutral-700">Le client a annulé ce RDV — le créneau a été libéré dans l'agenda.</p>
                  {rdv.cancel_reason && <p className="text-xs text-neutral-500">Motif : {rdv.cancel_reason}</p>}
                </div>
              )}
            </div>
          )}

          {/* Formulaire d'édition inline */}
          {editing && form && (
            <div className="border border-neutral-200 rounded-2xl p-4 space-y-3 bg-neutral-50/50" data-testid="rdv-fiche-edit-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-neutral-500">Type de RDV</label>
                  <select data-testid="rdv-fiche-edit-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={`${inputCls} mt-1`}>
                    <option value="visio">Visio</option>
                    <option value="telephonique">Téléphonique</option>
                    <option value="exterieur">Lieu extérieur</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-500">Date</label>
                  <input data-testid="rdv-fiche-edit-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={`${inputCls} mt-1`} />
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-500">Heure</label>
                  <input data-testid="rdv-fiche-edit-time" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className={`${inputCls} mt-1`} />
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-500">Durée du RDV (min)</label>
                  <input data-testid="rdv-fiche-edit-duration" type="number" min="5" step="5" value={form.rdv_minutes} onChange={(e) => setForm({ ...form, rdv_minutes: e.target.value })} className={`${inputCls} mt-1`} />
                </div>
                {form.type === "exterieur" && (
                  <>
                    <div>
                      <label className="text-xs font-medium text-neutral-500">Trajet aller (min)</label>
                      <input data-testid="rdv-fiche-edit-travel-before" type="number" min="0" value={form.travel_before_min} onChange={(e) => setForm({ ...form, travel_before_min: e.target.value })} className={`${inputCls} mt-1`} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-neutral-500">Trajet retour (min)</label>
                      <input data-testid="rdv-fiche-edit-travel-after" type="number" min="0" value={form.travel_after_min} onChange={(e) => setForm({ ...form, travel_after_min: e.target.value })} className={`${inputCls} mt-1`} />
                    </div>
                  </>
                )}
              </div>
              {form.type === "exterieur" && (
                <div>
                  <label className="text-xs font-medium text-neutral-500">Lieu *</label>
                  <input data-testid="rdv-fiche-edit-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Rue, ville" className={`${inputCls} mt-1`} />
                </div>
              )}
              {form.type === "visio" && (
                <div>
                  <label className="text-xs font-medium text-neutral-500">Lien visio</label>
                  <input data-testid="rdv-fiche-edit-visio" value={form.visio_link} onChange={(e) => setForm({ ...form, visio_link: e.target.value })} placeholder="https://meet.google.com/…" className={`${inputCls} mt-1`} />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-neutral-500">Nom du contact</label>
                  <input data-testid="rdv-fiche-edit-contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className={`${inputCls} mt-1`} />
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-500">{form.type === "telephonique" ? "Numéro à appeler" : "Téléphone"}</label>
                  <input data-testid="rdv-fiche-edit-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+41… / 06…" className={`${inputCls} mt-1`} />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-500">Email du contact</label>
                <input data-testid="rdv-fiche-edit-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${inputCls} mt-1`} />
              </div>
            </div>
          )}

          {/* Date / lieu */}
          {!editing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center shrink-0">
                  <CalendarDays className="w-4 h-4 text-[#64748B]" />
                </span>
                <div>
                  <p className="font-bold text-sm text-[#0F172A] capitalize" data-testid="rdv-fiche-date">{rdv.date ? cap(fmtDate(rdv.date)) : "—"}</p>
                  <p className="text-xs text-neutral-500 mt-0.5" data-testid="rdv-fiche-time">
                    {toHHMM(rdv.start_min)} – {toHHMM(rdv.start_min + rdvDur)} ({durLabel(rdvDur)})
                    {(rdv.travel_before_min || rdv.travel_after_min) ? ` · trajets ${rdv.travel_before_min}/${rdv.travel_after_min} min` : ""}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center shrink-0">
                  <loc.Icon className="w-4 h-4 text-[#64748B]" />
                </span>
                <div className="min-w-0">
                  <p className="font-bold text-sm text-[#0F172A]" data-testid="rdv-fiche-location">{loc.label}</p>
                  {loc.sub && (
                    loc.href ? (
                      <a href={loc.href} target={loc.href.startsWith("tel:") ? undefined : "_blank"} rel="noreferrer" data-testid="rdv-fiche-location-link"
                        className="text-xs text-[#0369A1] hover:underline flex items-center gap-1 mt-0.5 break-all">
                        {loc.sub} <ExternalLink className="w-3 h-3 shrink-0" />
                      </a>
                    ) : <p className="text-xs text-neutral-500 mt-0.5">{loc.sub}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Infos réservation en ligne */}
          {!editing && rdv.booked_online && (rdv.website || rdv.instagram || rdv.linkedin || rdv.company) && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-blue-700" data-testid="rdv-fiche-booking-info">
              {rdv.company && <span className="text-neutral-600 font-medium">{rdv.company}</span>}
              {rdv.website && <a href={/^https?:\/\//.test(rdv.website) ? rdv.website : `https://${rdv.website}`} target="_blank" rel="noreferrer" className="hover:underline">Site web</a>}
              {rdv.instagram && <a href={/^https?:\/\//.test(rdv.instagram) ? rdv.instagram : `https://instagram.com/${rdv.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer" className="hover:underline">Instagram</a>}
              {rdv.linkedin && <a href={/^https?:\/\//.test(rdv.linkedin) ? rdv.linkedin : `https://${rdv.linkedin}`} target="_blank" rel="noreferrer" className="hover:underline">LinkedIn</a>}
            </div>
          )}

          {/* Participants */}
          <section data-testid="rdv-fiche-participants">
            <h3 className="flex items-center gap-2 font-heading font-bold text-[15px] text-[#0F172A] mb-2.5">
              <Users className="w-4 h-4 text-neutral-500" /> Participants
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {(rdv.participants || []).map((p, i) => (
                <div key={i} className="flex items-center gap-3 group" data-testid={`rdv-fiche-participant-${i}`}>
                  <Avatar name={p.name} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#0F172A] truncate">{p.name}</p>
                    <p className="text-xs text-neutral-400 truncate">{p.role || "Participant"}</p>
                  </div>
                  <button onClick={() => removeParticipant(i)} data-testid={`rdv-fiche-participant-del-${i}`}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded text-neutral-300 hover:text-red-500 transition-all" title="Retirer">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            {addPartOpen ? (
              <div className="mt-3 border border-dashed border-neutral-300 rounded-xl p-3 space-y-2 bg-neutral-50/50" data-testid="rdv-fiche-participant-form">
                {contacts.length > 0 && (
                  <select data-testid="rdv-fiche-participant-contact" defaultValue=""
                    onChange={(e) => {
                      const c = contacts[Number(e.target.value)];
                      if (c) setPartForm({ name: c.name || "", role: c.role || c.fonction || c.poste || "" });
                    }}
                    className={inputCls}>
                    <option value="">Depuis les contacts du client…</option>
                    {contacts.map((c, i) => <option key={i} value={i}>{c.name}{(c.role || c.fonction || c.poste) ? ` — ${c.role || c.fonction || c.poste}` : ""}</option>)}
                  </select>
                )}
                <div className="flex flex-col sm:flex-row gap-2">
                  <input data-testid="rdv-fiche-participant-name" value={partForm.name} onChange={(e) => setPartForm({ ...partForm, name: e.target.value })}
                    placeholder="Nom (ex. Sarah Martin)" className={inputCls} />
                  <input data-testid="rdv-fiche-participant-role" value={partForm.role} onChange={(e) => setPartForm({ ...partForm, role: e.target.value })}
                    placeholder="Rôle (ex. DGA — Social Media)" className={inputCls}
                    onKeyDown={(e) => e.key === "Enter" && addParticipant()} />
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => { setAddPartOpen(false); setPartForm({ name: "", role: "" }); }} data-testid="rdv-fiche-participant-cancel"
                    className="text-xs font-semibold text-neutral-500 border border-neutral-200 rounded-lg px-3 py-1.5 hover:bg-neutral-50">Annuler</button>
                  <button onClick={addParticipant} data-testid="rdv-fiche-participant-add"
                    className="text-xs font-semibold bg-[#0B132B] text-white rounded-lg px-3 py-1.5 hover:bg-[#1C2A4F]">Ajouter</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAddPartOpen(true)} data-testid="rdv-fiche-add-participant"
                className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-neutral-500 border border-dashed border-neutral-300 rounded-full px-4 py-2 hover:border-neutral-400 hover:text-neutral-700 transition-colors">
                <UserPlus className="w-3.5 h-3.5" /> Ajouter un participant
              </button>
            )}
          </section>

          {/* Description */}
          <section data-testid="rdv-fiche-description-section">
            <h3 className="flex items-center gap-2 font-heading font-bold text-[15px] text-[#0F172A] mb-2.5">
              <FileText className="w-4 h-4 text-neutral-500" /> Description
            </h3>
            {editing ? (
              <textarea data-testid="rdv-fiche-edit-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Sujets à aborder, documents à préparer…" className={`${inputCls} resize-y`} />
            ) : (
              <p className="text-sm text-neutral-500 leading-relaxed whitespace-pre-line" data-testid="rdv-fiche-description">
                {rdv.description || "Aucune description."}
              </p>
            )}
          </section>

          {/* Pièces jointes */}
          <section data-testid="rdv-fiche-attachments">
            <div className="flex items-center gap-2 mb-2.5">
              <h3 className="flex items-center gap-2 font-heading font-bold text-[15px] text-[#0F172A]">
                <Paperclip className="w-4 h-4 text-neutral-500" /> Pièces jointes
              </h3>
              <input ref={fileRef} type="file" className="hidden" data-testid="rdv-fiche-file-input" onChange={(e) => uploadFile(e.target.files?.[0])} />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="rdv-fiche-upload"
                className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-[#0369A1] hover:underline disabled:opacity-50">
                <Upload className="w-3.5 h-3.5" /> {uploading ? "Envoi en cours…" : "Ajouter un fichier"}
              </button>
            </div>
            {(rdv.attachments || []).length === 0 && <p className="text-xs text-neutral-400" data-testid="rdv-fiche-attachments-empty">Aucune pièce jointe.</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {(rdv.attachments || []).map((att) => {
                const m = attMeta(att.name);
                return (
                  <div key={att.id} className="flex items-center gap-2.5 border border-neutral-100 rounded-xl px-3 py-2.5 hover:border-neutral-200 transition-colors" data-testid={`rdv-fiche-att-${att.id}`}>
                    <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: m.bg }}>
                      <m.Icon className="w-4 h-4" style={{ color: m.fg }} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-[#0F172A] truncate">{att.name}</p>
                      <p className="text-[10px] text-neutral-400">{m.label} · {fmtSize(att.size)}</p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`rdv-fiche-att-menu-${att.id}`} className="p-1 rounded-md hover:bg-neutral-100 text-neutral-400"><MoreHorizontal className="w-4 h-4" /></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem data-testid={`rdv-fiche-att-open-${att.id}`} onClick={() => window.open(attUrl(att), "_blank")}>Ouvrir</DropdownMenuItem>
                        <DropdownMenuItem data-testid={`rdv-fiche-att-dl-${att.id}`} onClick={() => window.open(attUrl(att, 1), "_blank")}>Télécharger</DropdownMenuItem>
                        <DropdownMenuItem data-testid={`rdv-fiche-att-del-${att.id}`} onClick={() => delAttachment(att)} className="text-red-600">Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Compte rendu */}
          <section data-testid="rdv-fiche-report-section">
            <div className="flex items-center gap-2 mb-2.5">
              <h3 className="flex items-center gap-2 font-heading font-bold text-[15px] text-[#0F172A]">
                <FileText className="w-4 h-4 text-neutral-500" /> Compte rendu
              </h3>
              <select value={reportStatus} onChange={(e) => setReportStatus(e.target.value)} data-testid="rdv-fiche-report-status"
                className={`appearance-none cursor-pointer text-[10px] font-semibold border rounded-full pl-2.5 pr-2 py-1 focus:outline-none ${REPORT_STATUS[reportStatus].cls}`}>
                {Object.entries(REPORT_STATUS).filter(([id]) => id !== "envoye" || reportStatus === "envoye").map(([id, o]) => (
                  <option key={id} value={id}>{o.label}</option>
                ))}
              </select>
              {reportSaved && (
                <span className="text-[10px] font-semibold text-emerald-600 flex items-center gap-1" data-testid="rdv-fiche-report-saved">
                  <Check className="w-3 h-3" /> Enregistré
                </span>
              )}
            </div>
            <div className="border border-neutral-200 rounded-xl overflow-hidden">
              <div className="flex items-center gap-0.5 px-2.5 py-1.5 border-b border-neutral-100 bg-neutral-50/60">
                <ToolBtn icon={Bold} label="Gras" testid="rdv-fiche-fmt-bold" onClick={() => wrapSelection("**")} />
                <ToolBtn icon={Italic} label="Italique" testid="rdv-fiche-fmt-italic" onClick={() => wrapSelection("*")} />
                <ToolBtn icon={List} label="Liste à puces" testid="rdv-fiche-fmt-bullets" onClick={() => prefixLines(() => "• ")} />
                <ToolBtn icon={ListOrdered} label="Liste numérotée" testid="rdv-fiche-fmt-numbered" onClick={() => prefixLines((i) => `${i + 1}. `)} />
                <ToolBtn icon={Link2} label="Lien" testid="rdv-fiche-fmt-link" onClick={() => wrapSelection("[", "](https://)")} />
              </div>
              <textarea ref={reportRef} data-testid="rdv-fiche-report" rows={6} value={report}
                onChange={(e) => setReport(e.target.value)} onBlur={saveReport}
                placeholder="Rédigez le compte rendu du rendez-vous… (enregistré automatiquement)"
                className="w-full text-sm px-3.5 py-3 focus:outline-none resize-y leading-relaxed text-neutral-600" />
            </div>
          </section>

          {/* Actions avant / après */}
          <ActionSection title="À préparer avant le RDV" which="actions_before" rdv={rdv}
            draft={drafts.actions_before} onDraft={onDraft} onAdd={addAction} onToggle={toggleAction} onRemove={removeAction} />
          <ActionSection title="Actions / Prochaines étapes" which="actions_after" rdv={rdv}
            draft={drafts.actions_after} onDraft={onDraft} onAdd={addAction} onToggle={toggleAction} onRemove={removeAction} />
        </div>

        {/* Pied de fiche */}
        <div className="sticky bottom-0 bg-white border-t border-neutral-100 px-6 sm:px-8 py-3.5 flex items-center gap-2 rounded-b-2xl">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="rdv-fiche-more" className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-600 border border-neutral-200 rounded-xl px-3 py-2 hover:bg-neutral-50 transition-colors">
                Plus d'actions <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem data-testid="rdv-fiche-duplicate" onClick={duplicateRdv}>
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Dupliquer
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="rdv-fiche-postpone" onClick={startEdit}>
                <CalendarClock className="w-3.5 h-3.5 mr-1.5" /> Reporter
              </DropdownMenuItem>
              {status !== "annule" ? (
                <DropdownMenuItem data-testid="rdv-fiche-cancel" onClick={cancelRdv} className="text-red-600">
                  <XCircle className="w-3.5 h-3.5 mr-1.5" /> Annuler le RDV
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem data-testid="rdv-fiche-reactivate" onClick={() => setStatus("a_venir")}>
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Réactiver le RDV
                </DropdownMenuItem>
              )}
              {rdv.block_id && (
                <DropdownMenuItem data-testid="rdv-fiche-goto-agenda" onClick={() => { onClose(); navigate(`/agenda?week=${mondayISO(rdv.date)}&highlight=${rdv.block_id}`); }}>
                  <CalendarRange className="w-3.5 h-3.5 mr-1.5" /> Voir dans l'agenda
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="rdv-fiche-delete" onClick={remove} className="text-red-600">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Supprimer le RDV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            {editing ? (
              <>
                <button onClick={() => setEditing(false)} data-testid="rdv-fiche-cancel-edit"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-600 border border-neutral-200 rounded-xl px-3.5 py-2 hover:bg-neutral-50 transition-colors">
                  Annuler
                </button>
                <button onClick={saveEdit} disabled={saving} data-testid="rdv-fiche-save"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#0B132B] text-white rounded-xl px-4 py-2 hover:bg-[#1C2A4F] transition-colors disabled:opacity-50">
                  <Check className="w-3.5 h-3.5" /> {saving ? "Enregistrement…" : "Enregistrer"}
                </button>
              </>
            ) : (
              <button onClick={startEdit} data-testid="rdv-fiche-edit"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-600 border border-neutral-200 rounded-xl px-3.5 py-2 hover:bg-neutral-50 transition-colors">
                <Pencil className="w-3.5 h-3.5" /> Modifier
              </button>
            )}
            {status !== "termine" ? (
              <button onClick={() => setStatus("termine")} data-testid="rdv-fiche-complete"
                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#0B132B] text-white rounded-xl px-4 py-2 hover:bg-[#1C2A4F] transition-colors">
                <CheckCircle2 className="w-4 h-4" /> Marquer comme terminé
              </button>
            ) : (
              <button onClick={() => setStatus("en_cours")} data-testid="rdv-fiche-reopen"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-600 border border-neutral-200 rounded-xl px-3.5 py-2 hover:bg-neutral-50 transition-colors">
                <RotateCcw className="w-3.5 h-3.5" /> Rouvrir le RDV
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
